const DEFAULT_COLOR = "#808080";

const normalizeKey = (value?: string | null) =>
  value ? value.toUpperCase().replace(/\s+/g, " ").trim() : "";

export const STORE_COLOR_MAP: Record<string, string> = {
  VLS: "#e34e76",
  BOLO: "#007887",
  MAR: "#8773aa",
  BSM: "#e6e1ff",
  SMA: "#fe3e3e",
  VBS: "#ffff78",
  SLP: "#d2e6ff",
  TRS: "#d2d2b2",
  FHS: "#5fd0ae",
  EDP: "#ff8dfa",
  LDS: "#1e3a8a",
  JVA: "#e0a100",
  LYW: "#2e8b3a",
  ASW: "#7b5233",
  ODP: "#00acc1",
  RDJ: "#8a9a1b",
  TOTAL: "#c2410c",
};

export const CITY_COLOR_MAP: Record<string, string> = {
  AMSTERDAM: "#d2e6ff",
  UTRECHT: "#007887",
  "DEN HAAG": "#ffff78",
  ROTTERDAM: "#5ce1e6",
  HAARLEM: "#e34e76",
  EINDHOVEN: "#10B981",
  GRONINGEN: "#F59E0B",
  TILBURG: "#8773aa",
  TOTAL: "#c2410c",
};

export const MATERIAL_COLOR_MAP: Record<string, string> = {
  TEXTILES: "#5c4d7d",
  EWASTE: "#b91c3c",
  "E-WASTE": "#b91c3c",
  "E WASTE": "#b91c3c",
  "ELECTRONIC WASTE": "#b91c3c",
  "PLASTIC VERPAKKINGEN": "#1d6fd9",
  OILS: "#8b6fd4",
  STATIE: "#0d5c63",
  MELKFLESSEN: "#0d9488",
  "MELK FLESSEN": "#0d9488",
  "MILK BOTTLES": "#0d9488",
  "MILK JERRYCANS": "#0d9488",
  "MELKFLESSEN (HDPE2)": "#0d9488",
  HDPE2: "#0d9488",
  "FLATTEN CANS": "#047857",
  "FLATTENED CANS": "#047857",
  CANS: "#047857",
  FLATTEN: "#047857",
  // Materials counted per item, toys and coffee cups: each its own colour, so
  // no two show the same grey, and dark enough for the white bar labels. A
  // material without an entry here is grey.
  "SMARTPHONES / TABLETS": "#ad771b",
  "LAPTOPS / FLATSCREENS": "#532bc7",
  "LARGE HOUSEHOLD APPLIANCES": "#8a2b60",
  "ELECTRIC TOOTHBRUSH": "#bf5c8f",
  "INK CARTRIDGES": "#732a9d",
  "BOOKS": "#7a4702",
  "KID'S BIKES": "#a516a0",
  "HELMET": "#154fac",
  "SLEEPING BAG": "#756f1d",
  "FOOD FOR SKIN": "#14769a",
  "IKEA PRODUCTS": "#833cd5",
  "DUPLO TOYS": "#a45a4e",
  "LEGO TOYS": "#8e5192",
  "WOODEN TOYS": "#62950e",
  "WOODEN MUSICAL INSTRUMENT": "#ba48d3",
  "PUZZLE": "#3e6102",
  "GAMES/BOARD GAMES": "#e1441a",
  "KOFFIECUPS ALUMINIUM": "#dd226c",
  "KOFFIECUPS PLASTIC": "#5985be",
  TOTAL: "#c2410c",
  "TOTAL ALL STORES": "#c2410c",
};

/** Daha kontrastlı renkler - sadece Total bar segmentleri için */
export const MATERIAL_CONTRAST_COLOR_MAP: Record<string, string> = {
  TEXTILES: "#3d3366",
  EWASTE: "#7a0f2e",
  "E-WASTE": "#7a0f2e",
  "E WASTE": "#7a0f2e",
  "ELECTRONIC WASTE": "#7a0f2e",
  "PLASTIC VERPAKKINGEN": "#0a3d8c",
  OILS: "#4a2d8a",
  STATIE: "#054040",
  MELKFLESSEN: "#076b5f",
  "MELK FLESSEN": "#076b5f",
  "MILK BOTTLES": "#076b5f",
  "MILK JERRYCANS": "#076b5f",
  "MELKFLESSEN (HDPE2)": "#076b5f",
  HDPE2: "#076b5f",
  "FLATTEN CANS": "#065f46",
  "FLATTENED CANS": "#065f46",
  CANS: "#065f46",
  FLATTEN: "#065f46",
  // Materials counted per item, toys and coffee cups (darker, for the total bar).
  "SMARTPHONES / TABLETS": "#7a5208",
  "LAPTOPS / FLATSCREENS": "#310583",
  "LARGE HOUSEHOLD APPLIANCES": "#5a0039",
  "ELECTRIC TOOTHBRUSH": "#913266",
  "INK CARTRIDGES": "#480069",
  "BOOKS": "#482700",
  "KID'S BIKES": "#6c0268",
  "HELMET": "#002a6f",
  "SLEEPING BAG": "#4c4700",
  "FOOD FOR SKIN": "#044c66",
  "IKEA PRODUCTS": "#5a099e",
  "DUPLO TOYS": "#773228",
  "LEGO TOYS": "#642968",
  "WOODEN TOYS": "#426800",
  "WOODEN MUSICAL INSTRUMENT": "#8d0ba5",
  "PUZZLE": "#213800",
  "GAMES/BOARD GAMES": "#a12a07",
  "KOFFIECUPS ALUMINIUM": "#9c0848",
  "KOFFIECUPS PLASTIC": "#315c91",
};

export const PARCEL_COURIER_COLOR_MAP: Record<string, string> = {
  DHL: "#ffcb00",
  DPD: "#db0032",
  MONDIAL: "#ac0148",
  BUDBEE: "#41cda5",
  "VINTED GO": "#004857",
  vintedgo: "#004857",
  HOMERR: "#004857",
  UPS: "#301406",
  TOTAL: "#c2410c",
};

/** Total serisi için daha kontrastlı renk (line chart'larda) */
export const TOTAL_CONTRAST_COLOR = "#8b2e0a";

export const getStoreColor = (
  value?: string | null,
  fallback = DEFAULT_COLOR
) => {
  const normalized = normalizeKey(value);
  if (!normalized) return fallback;
  if (STORE_COLOR_MAP[normalized]) return STORE_COLOR_MAP[normalized];
  const match = Object.entries(STORE_COLOR_MAP).find(([key]) =>
    normalized.includes(key)
  );
  return match?.[1] ?? fallback;
};

export const getCityColor = (
  value?: string | null,
  fallback = DEFAULT_COLOR
) => {
  const normalized = normalizeKey(value);
  if (!normalized) return fallback;
  if (CITY_COLOR_MAP[normalized]) return CITY_COLOR_MAP[normalized];
  const match = Object.entries(CITY_COLOR_MAP).find(([key]) =>
    normalized.includes(key)
  );
  return match?.[1] ?? fallback;
};

export const getMaterialColor = (
  value?: string | null,
  fallback = DEFAULT_COLOR
) => {
  const normalized = normalizeKey(value);
  if (!normalized) return fallback;
  if (MATERIAL_COLOR_MAP[normalized]) return MATERIAL_COLOR_MAP[normalized];
  const match = Object.entries(MATERIAL_COLOR_MAP).find(([key]) =>
    normalized.includes(key)
  );
  return match?.[1] ?? fallback;
};

export const getMaterialContrastColor = (
  value?: string | null,
  fallback = "#404040"
) => {
  const normalized = normalizeKey(value);
  if (!normalized) return fallback;
  if (MATERIAL_CONTRAST_COLOR_MAP[normalized])
    return MATERIAL_CONTRAST_COLOR_MAP[normalized];
  const match = Object.entries(MATERIAL_CONTRAST_COLOR_MAP).find(([key]) =>
    normalized.includes(key)
  );
  return match?.[1] ?? fallback;
};

export const getParcelCourierColor = (
  value?: string | null,
  fallback = DEFAULT_COLOR
) => {
  const normalized = normalizeKey(value);
  if (!normalized) return fallback;
  if (PARCEL_COURIER_COLOR_MAP[normalized])
    return PARCEL_COURIER_COLOR_MAP[normalized];
  const match = Object.entries(PARCEL_COURIER_COLOR_MAP).find(([key]) =>
    normalized.includes(key)
  );
  return match?.[1] ?? fallback;
};

const COMMUNITY_COLOR_PALETTE = [
  "#23C16B",
  "#FFB323",
  "#48A7F8",
  "#FF3333",
  "#FF8800",
  "#9259FF",
  "#FF4DA6",
  "#33FFE6",
  "#D17A50",
  "#10B981",
  "#F59E0B",
  "#6366F1",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#8B5CF6",
  "#EF4444",
  "#06B6D4",
  "#84CC16",
  "#E11D48",
  "#0EA5E9",
  "#A855F7",
  "#22D3EE",
  "#A3E635",
  "#FB7185",
];

const simpleHash = (str: string): number => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

export const getCommunityColor = (
  value?: string | null,
  fallback = DEFAULT_COLOR
) => {
  if (!value || value.trim() === "") return fallback;
  const hash = simpleHash(value.trim());
  const index = hash % COMMUNITY_COLOR_PALETTE.length;
  return COMMUNITY_COLOR_PALETTE[index];
};
